if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
        
        // Initialize B2B gallery navigation
        this.initB2BNavigation();
      }

      initB2BNavigation() {
        const isB2BView = this.dataset.galleryView === 'b2b-gallery';
        if (!isB2BView) return;

        const navButtons = this.querySelectorAll('.b2b-nav-button');
        if (!navButtons.length) return;

        const prevButton = this.querySelector('.b2b-nav-button--prev');
        const nextButton = this.querySelector('.b2b-nav-button--next');

        if (prevButton) {
          prevButton.addEventListener('click', () => this.navigateB2BGallery('prev'));
        }
        if (nextButton) {
          nextButton.addEventListener('click', () => this.navigateB2BGallery('next'));
        }
      }

      navigateB2BGallery(direction) {
        const thumbnails = Array.from(this.elements.thumbnails.querySelectorAll('[data-target]'));
        const currentActiveButton = this.elements.thumbnails.querySelector('[aria-current="true"]');
        
        if (!currentActiveButton || thumbnails.length === 0) return;
        
        // Find the parent li element that contains the button
        const currentActive = currentActiveButton.closest('[data-target]');
        const currentIndex = thumbnails.indexOf(currentActive);

        let newIndex;
        if (direction === 'prev') {
          newIndex = currentIndex > 0 ? currentIndex - 1 : thumbnails.length - 1;
        } else {
          newIndex = currentIndex < thumbnails.length - 1 ? currentIndex + 1 : 0;
        }

        const targetThumbnail = thumbnails[newIndex];
        if (targetThumbnail) {
          const targetMediaId = targetThumbnail.dataset.target;
          this.setActiveMedia(targetMediaId, false);
        }
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        
        const isB2BView = this.dataset.galleryView === 'b2b-gallery';
        
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');
        activeMedia.classList.remove('scroll-trigger--offscreen');
        
        // For B2B view, also show the next thumbnail's image
        if (isB2BView && this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
          if (activeThumbnail) {
            const allThumbnails = Array.from(this.elements.thumbnails.querySelectorAll('[data-target]'));
            const thumbnailIndex = allThumbnails.indexOf(activeThumbnail);
            
            if (thumbnailIndex >= 0 && thumbnailIndex < allThumbnails.length - 1) {
              const nextThumbnail = allThumbnails[thumbnailIndex + 1];
              const nextMediaId = nextThumbnail.dataset.target;
              const nextMedia = this.elements.viewer.querySelector(`[data-media-id="${nextMediaId}"]`);
              
              if (nextMedia) {
                nextMedia.classList.add('is-active');
                nextMedia.classList.remove('scroll-trigger--offscreen');
              }
            }
          }
        }

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft, behavior: 'smooth' });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
